import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Ghost,
  Play,
  RotateCcw,
  Skull,
  XCircle,
  Zap,
} from 'lucide-react';
import { learningApi } from '../api/learning';
import { ArcadeSummaryView } from '../components/ui';
import { ApiRequestError } from '@zolotalk/api-client';
import type { ArcadeAnswerResult, GhostQuestion, GhostRun } from '@zolotalk/api-client';
import './BeatGhostPage.css';

type Phase = 'setup' | 'playing' | 'complete';

interface GameAnswer extends ArcadeAnswerResult {
  question: GhostQuestion;
  submittedAnswer: string;
}

function ghostProgressPercent(ghost: { totalMs: number } | null, currentElapsed: number): number {
  if (!ghost || ghost.totalMs <= 0) return 0;
  return Math.min(100, Math.round((currentElapsed / ghost.totalMs) * 100));
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function countdownText(deadlineAt: string | null): string {
  if (!deadlineAt) return '';
  const remaining = new Date(deadlineAt).getTime() - Date.now();
  if (remaining <= 0) return '0:00';
  const secs = Math.ceil(remaining / 1000);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function AnswerFeedback({ answer }: { answer: GameAnswer }) {
  return (
    <div
      className={`ghost-feedback ${answer.correct ? 'ghost-feedback--correct' : 'ghost-feedback--incorrect'}`}
      role="status"
    >
      {answer.correct ? (
        <>
          <CheckCircle2 aria-hidden="true" />
          <span>
            Correct! +{answer.scoreDelta} pts
            {answer.combo > 1 && <span className="ghost-combo-badge">x{answer.combo} combo</span>}
          </span>
        </>
      ) : (
        <>
          <XCircle aria-hidden="true" />
          <span>
            Incorrect — <span className="ghost-correct-answer">{answer.correctAnswer}</span>
          </span>
        </>
      )}
    </div>
  );
}

function GhostBar({ ghost, yourElapsed }: { ghost: GhostRun['ghostTrace']; yourElapsed: number }) {
  if (!ghost) return null;
  const pct = ghostProgressPercent(ghost, yourElapsed);
  return (
    <div className="ghost-bar" role="progressbar" aria-label="Ghost comparison" aria-valuenow={pct}>
      <div className="ghost-bar-track">
        <div className="ghost-bar-fill" style={{ width: `${Math.min(100, pct)}%` }} />
        <Ghost className="ghost-bar-icon" size={16} aria-hidden="true" />
      </div>
      <span className="ghost-bar-label">Ghost: {formatMs(ghost.totalMs)}</span>
    </div>
  );
}

function GhostSetup({
  onStart,
  loading,
  error,
  onClearError,
}: {
  onStart: (mode: 'rapid_fire' | 'survival', timing: 'standard' | 'relaxed') => void;
  loading: boolean;
  error: string | null;
  onClearError: () => void;
}) {
  const [mode, setMode] = useState<'rapid_fire' | 'survival'>('rapid_fire');
  const [timing, setTiming] = useState<'standard' | 'relaxed'>('standard');

  return (
    <div className="ghost-setup container">
      <header className="ghost-header">
        <span className="ghost-eyebrow">
          <Ghost size={20} aria-hidden="true" /> Beat Your Ghost
        </span>
        <h1>Race your personal best</h1>
        <p>Play against your own best run. Answer each question faster than your ghost to win.</p>
      </header>

      {error && (
        <div className="ghost-error" role="alert">
          <AlertCircle aria-hidden="true" />
          <span>{error}</span>
          <button className="ghost-dismiss-error" onClick={onClearError}>
            Dismiss
          </button>
        </div>
      )}

      <div className="ghost-card">
        <div className="ghost-option-group">
          <label className="ghost-label" htmlFor="ghost-mode">
            Mode
          </label>
          <div className="ghost-toggle" role="radiogroup" aria-label="Game mode">
            <button
              id="ghost-mode"
              role="radio"
              aria-checked={mode === 'rapid_fire'}
              onClick={() => setMode('rapid_fire')}
            >
              <Clock aria-hidden="true" /> Rapid Fire
            </button>
            <button
              role="radio"
              aria-checked={mode === 'survival'}
              onClick={() => setMode('survival')}
            >
              <Skull aria-hidden="true" /> Survival
            </button>
          </div>
        </div>

        <div className="ghost-option-group">
          <label className="ghost-label" htmlFor="ghost-timing">
            Timing
          </label>
          <div className="ghost-toggle" role="radiogroup" aria-label="Timing">
            <button
              id="ghost-timing"
              role="radio"
              aria-checked={timing === 'standard'}
              onClick={() => setTiming('standard')}
            >
              <Zap aria-hidden="true" /> Standard
            </button>
            <button
              role="radio"
              aria-checked={timing === 'relaxed'}
              onClick={() => setTiming('relaxed')}
            >
              <RotateCcw aria-hidden="true" /> Relaxed
            </button>
          </div>
        </div>

        <button
          className="ghost-start-button"
          disabled={loading}
          onClick={() => {
            onClearError();
            onStart(mode, timing);
          }}
        >
          <Play aria-hidden="true" /> {loading ? 'Starting…' : 'Beat Your Ghost'}
        </button>
      </div>
    </div>
  );
}

function GhostPlaying({
  run,
  onAnswer,
  onComplete,
}: {
  run: GhostRun;
  onAnswer: (answer: GameAnswer) => void;
  onComplete: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answerText, setAnswerText] = useState('');
  const [lastAnswer, setLastAnswer] = useState<GameAnswer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startTime = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    startTime.current = Date.now();
    inputRef.current?.focus();
    const interval = window.setInterval(() => {
      setElapsed(Math.max(0, Date.now() - startTime.current));
    }, 250);
    return () => window.clearInterval(interval);
  }, []);

  const question = run.questions[currentIndex];
  const ghost = run.ghostTrace;

  const submitAnswer = useCallback(
    async (answer: string) => {
      if (!question || submitting) return;
      setSubmitting(true);
      setError(null);
      try {
        const result = await learningApi.answerArcadeQuestion(run.runId, {
          questionId: question.id,
          answer,
        });
        const gameAnswer: GameAnswer = { ...result, question, submittedAnswer: answer };
        setLastAnswer(gameAnswer);
        onAnswer(gameAnswer);

        if (result.complete || result.status !== 'in_progress') {
          await new Promise(r => setTimeout(r, 800));
          onComplete();
        } else {
          setCurrentIndex(prev => prev + 1);
          setAnswerText('');
          inputRef.current?.focus();
        }
      } catch {
        setError('Failed to submit answer. Try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [question, submitting, run.runId, onAnswer, onComplete]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!answerText.trim()) return;
    submitAnswer(answerText.trim());
  };

  if (!question) {
    return (
      <div role="status" className="ghost-loading">
        Loading questions…
      </div>
    );
  }

  const ghostEntry = ghost?.entries[currentIndex];

  return (
    <div className="ghost-playing container">
      <div className="ghost-progress-header">
        <span className="ghost-progress-text">
          Question {currentIndex + 1} of {run.totalQuestions}
        </span>
        {run.deadlineAt && (
          <span className="ghost-timer">
            <Clock size={16} aria-hidden="true" /> {countdownText(run.deadlineAt)}
          </span>
        )}
      </div>

      <GhostBar ghost={ghost} yourElapsed={elapsed} />

      <div className="ghost-question-card">
        <p className="ghost-prompt">{question.prompt}</p>

        {question.type === 'word-match' && 'options' in question && question.options && (
          <div className="ghost-options">
            {(question.options as string[]).map(option => (
              <button
                key={option}
                className={`ghost-option-button ${answerText === option ? 'ghost-option-button--selected' : ''}`}
                onClick={() => setAnswerText(option)}
                disabled={submitting}
              >
                {option}
              </button>
            ))}
            {answerText && (
              <button
                className="ghost-submit-answer"
                disabled={submitting}
                onClick={() => submitAnswer(answerText)}
              >
                {submitting ? 'Grading…' : 'Submit Answer'}
              </button>
            )}
          </div>
        )}

        {question.type === 'fill-in-blank' && (
          <form onSubmit={handleSubmit} className="ghost-blank-form">
            <p className="ghost-sentence" lang="en">
              {'sentence' in question && question.sentence}
            </p>
            <label className="visually-hidden" htmlFor="ghost-fill-in-answer">
              Your answer
            </label>
            <div className="ghost-input-row">
              <input
                id="ghost-fill-in-answer"
                ref={inputRef}
                className="ghost-text-input"
                type="text"
                value={answerText}
                onChange={e => setAnswerText(e.target.value)}
                placeholder="Type your answer…"
                disabled={submitting}
                autoComplete="off"
              />
              <button
                className="ghost-submit-answer"
                type="submit"
                disabled={submitting || !answerText.trim()}
              >
                {submitting ? 'Grading…' : 'Submit'}
              </button>
            </div>
          </form>
        )}

        {ghostEntry && (
          <div className="ghost-comparison">
            <Ghost size={14} aria-hidden="true" />
            <span>
              Ghost completed this in {formatMs(ghostEntry.elapsedMs)}
              {ghostEntry.correct ? ' (correct)' : ' (incorrect)'}
            </span>
          </div>
        )}
      </div>

      {lastAnswer && <AnswerFeedback answer={lastAnswer} />}

      {error && (
        <div className="ghost-notice ghost-notice--error" role="alert">
          <AlertCircle aria-hidden="true" /> {error}
        </div>
      )}
    </div>
  );
}

function GhostComplete({
  answers,
  ghost,
  run,
  onRestart,
}: {
  answers: GameAnswer[];
  ghost: GhostRun['ghostTrace'];
  run: GhostRun;
  onRestart: () => void;
}) {
  const correct = answers.filter(a => a.correct).length;
  const total = answers.length;
  const accuracy = total > 0 ? correct / total : 0;
  const lastAnswer = answers[answers.length - 1];
  const finalScore = lastAnswer?.verifiedScore ?? run.personalBest ?? 0;
  // NR-4: render the server's comparison verdict; never reconstruct the
  // ghost's score from client-visible trace fields.
  const ghostOutcome = lastAnswer?.ghostOutcome ?? null;
  const ghostOutcomeLabel =
    ghostOutcome === 'beat'
      ? 'Beat'
      : ghostOutcome === 'tied'
        ? 'Tied'
        : ghostOutcome === 'behind'
          ? 'Behind'
          : '—';

  return (
    <ArcadeSummaryView
      className="ghost-complete container"
      heading="Run Complete!"
      headingLevel="h1"
      verifiedScore={finalScore}
      accuracy={accuracy}
      correctCount={correct}
      answeredCount={total}
      isNewPersonalBest={lastAnswer?.isNewPersonalBest ?? false}
      additionalStats={[
        {
          label: 'vs Ghost',
          value: (
            <>
              <Ghost size={20} aria-hidden="true" /> {ghostOutcomeLabel}
            </>
          ),
        },
      ]}
      playAgainLabel="Play Again"
      onPlayAgain={onRestart}
    >
      <div className="ghost-answers-review">
        <h3>Answer Review</h3>
        {answers.map((a, i) => {
          const ghostEntry = ghost?.entries[i];
          return (
            <div
              key={a.questionId}
              className={`ghost-review-row ${a.correct ? 'ghost-review-row--correct' : 'ghost-review-row--incorrect'}`}
            >
              <div className="ghost-review-q">
                <span className="ghost-review-num">{i + 1}.</span>
                {a.correct ? (
                  <CheckCircle2 size={18} aria-hidden="true" />
                ) : (
                  <XCircle size={18} aria-hidden="true" />
                )}
                <span>{a.question.prompt}</span>
              </div>
              <div className="ghost-review-detail">
                <span>
                  Your answer: <strong>{a.submittedAnswer}</strong>
                </span>
                {!a.correct && (
                  <span>
                    Correct answer: <strong>{a.correctAnswer}</strong>
                  </span>
                )}
                {ghostEntry && (
                  <span className="ghost-review-ghost">
                    Ghost: {formatMs(ghostEntry.elapsedMs)}
                  </span>
                )}
                <span>+{a.scoreDelta} pts</span>
              </div>
            </div>
          );
        })}
      </div>
    </ArcadeSummaryView>
  );
}

export function BeatGhostPage() {
  const [phase, setPhase] = useState<Phase>('setup');
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState<GhostRun | null>(null);
  const [answers, setAnswers] = useState<GameAnswer[]>([]);
  const [error, setError] = useState<string | null>(null);

  const runRef = useRef<GhostRun | null>(null);
  const finalizedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    runRef.current = run;
    if (run && phase !== 'playing') {
      finalizedRef.current = true;
    } else if (!run) {
      finalizedRef.current = true;
    } else {
      finalizedRef.current = false;
    }
  }, [run, phase]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const currentRun = runRef.current;
      if (currentRun && !finalizedRef.current) {
        learningApi.abandonArcadeRun(currentRun.runId).catch(() => {});
      }
    };
  }, []);

  const handleStart = useCallback(
    async (mode: 'rapid_fire' | 'survival', timing: 'standard' | 'relaxed') => {
      setLoading(true);
      setError(null);
      try {
        const result = await learningApi.createGhostRun({ mode, timing, limit: 12 });
        // The run only gets an id here, so a create still in flight at unmount is
        // invisible to the cleanup effect above. Abandon it on this path instead.
        if (!mountedRef.current) {
          learningApi.abandonArcadeRun(result.runId).catch(() => {});
          return;
        }
        setRun(result);
        setAnswers([]);
        setPhase('playing');
      } catch (err) {
        const status =
          err instanceof ApiRequestError
            ? err.status
            : typeof (err as Record<string, unknown>)?.['status'] === 'number'
              ? (err as Record<string, number>)['status']
              : 0;
        if (status === 401) {
          setError('Sign in to race your personal best.');
        } else if (status === 429) {
          setError('Too many attempts. Wait a moment then try again.');
        } else {
          setError('Could not start the ghost run. Check your connection and try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleComplete = useCallback(() => {
    finalizedRef.current = true;
    setPhase('complete');
  }, []);

  const handleRestart = useCallback(() => {
    setPhase('setup');
    setRun(null);
    setAnswers([]);
    setError(null);
  }, []);

  if (phase === 'setup') {
    return (
      <GhostSetup
        onStart={handleStart}
        loading={loading}
        error={error}
        onClearError={() => setError(null)}
      />
    );
  }
  if (phase === 'complete' && run) {
    return (
      <GhostComplete answers={answers} ghost={run.ghostTrace} run={run} onRestart={handleRestart} />
    );
  }
  if (phase === 'playing' && run) {
    return (
      <GhostPlaying
        run={run}
        onAnswer={answer => setAnswers(prev => [...prev, answer])}
        onComplete={handleComplete}
      />
    );
  }
  return <GhostSetup onStart={handleStart} loading={false} error={null} onClearError={() => {}} />;
}
